import React, { useEffect, useState } from 'react';
import { Users as UsersIcon, Plus, Pencil, Trash2, Lock, Unlock } from 'lucide-react';
import '../pages.css';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/States';
import { useToast } from '../../components/ui/Toast';
import { useAuth, authHeaders } from '../../auth';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

const EMPTY_NOTIFICATIONS = { email: '', telegramChatId: '', channels: { inApp: true, telegram: false, email: false } };
const EMPTY_FORM = { email: '', name: '', password: '', role: 'user', plantIds: [], notifications: EMPTY_NOTIFICATIONS };

export default function UsersPage() {
  const { token } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState(null);
  const [plants, setPlants] = useState([]);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [originalPlantIds, setOriginalPlantIds] = useState([]);
  const [plantsLoading, setPlantsLoading] = useState(false);

  const headers = { 'Content-Type': 'application/json', ...authHeaders(token) };

  const load = async () => {
    setError(false);
    try {
      const [usersRes, plantsRes] = await Promise.all([
        fetch(`${API_BASE}/admin/users`, { headers }),
        fetch(`${API_BASE}/plants`, { headers })
      ]);
      if (!usersRes.ok || !plantsRes.ok) throw new Error('load failed');
      setUsers(await usersRes.json());
      setPlants(await plantsRes.json());
    } catch {
      setUsers([]);
      setError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingUser(null); setForm(EMPTY_FORM); setOriginalPlantIds([]); setShowForm(true); };
  const openEdit = async (user) => {
    setEditingUser(user);
    setForm({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      plantIds: [],
      notifications: {
        email: user.notifications?.email || '',
        telegramChatId: user.notifications?.telegramChatId || '',
        channels: {
          inApp: user.notifications?.channels?.inApp ?? true,
          telegram: user.notifications?.channels?.telegram ?? false,
          email: user.notifications?.channels?.email ?? false
        }
      }
    });
    setShowForm(true);
    setPlantsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}/plants`, { headers });
      const data = res.ok ? await res.json() : { plantIds: [] };
      const assigned = Array.isArray(data.plantIds) ? data.plantIds : [];
      setOriginalPlantIds(assigned);
      setForm(f => ({ ...f, plantIds: assigned }));
    } catch {
      setOriginalPlantIds([]);
    } finally {
      setPlantsLoading(false);
    }
  };
  const closeForm = () => { setShowForm(false); setEditingUser(null); setForm(EMPTY_FORM); setOriginalPlantIds([]); };

  const updateNotifField = (field, value) => {
    setForm(f => ({ ...f, notifications: { ...f.notifications, [field]: value } }));
  };
  const toggleChannel = (channel) => {
    setForm(f => ({ ...f, notifications: { ...f.notifications, channels: { ...f.notifications.channels, [channel]: !f.notifications.channels[channel] } } }));
  };

  const togglePlant = (plantId) => {
    setForm(f => ({
      ...f,
      plantIds: f.plantIds.includes(plantId) ? f.plantIds.filter(id => id !== plantId) : [...f.plantIds, plantId]
    }));
  };

  // The backend has no "replace all assignments" endpoint, only
  // assign/unassign single plant - so editing diffs the checkbox selection
  // against what was loaded when the form opened and issues one call per
  // added/removed plant.
  const syncAssignments = async (userId) => {
    const added = form.plantIds.filter(id => !originalPlantIds.includes(id));
    const removed = originalPlantIds.filter(id => !form.plantIds.includes(id));
    await Promise.all([
      ...added.map(plantId => fetch(`${API_BASE}/admin/assignments`, { method: 'POST', headers, body: JSON.stringify({ userId, plantId }) })),
      ...removed.map(plantId => fetch(`${API_BASE}/admin/assignments/${userId}/${plantId}`, { method: 'DELETE', headers }))
    ]);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const body = { name: form.name, role: form.role, notifications: form.notifications };
        if (form.password) body.password = form.password;
        const res = await fetch(`${API_BASE}/admin/users/${editingUser.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
        if (!res.ok) throw new Error((await res.json()).error || 'Erreur de mise à jour');
        await syncAssignments(editingUser.id);
        toast.success('Utilisateur mis à jour');
      } else {
        const res = await fetch(`${API_BASE}/admin/users`, { method: 'POST', headers, body: JSON.stringify(form) });
        if (!res.ok) throw new Error((await res.json()).error || 'Erreur de création');
        toast.success('Utilisateur créé');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const confirmDelete = async () => {
    const userId = deleteTarget;
    setDeleteTarget(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${userId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur de suppression');
      toast.success('Utilisateur supprimé');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleStatus = async (user) => {
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}/status`, { method: 'PATCH', headers, body: JSON.stringify({ active: !user.active }) });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur');
      toast.success(user.active ? 'Utilisateur désactivé' : 'Utilisateur activé');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Utilisateurs' }]} />
      <div className="page-header">
        <h1><UsersIcon size={22} /> Utilisateurs</h1>
        <button className="btn-primary" onClick={openCreate}><Plus size={16} style={{ verticalAlign: 'middle' }} /> Nouvel Utilisateur</button>
      </div>

      {users === null ? (
        <LoadingState label="Chargement des utilisateurs..." />
      ) : error ? (
        <ErrorState label="Impossible de charger les utilisateurs" />
      ) : users.length === 0 ? (
        <EmptyState label="Aucun utilisateur" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr><th>Email</th><th>Nom</th><th>Rôle</th><th>Statut</th><th>Créé le</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td>{user.role === 'admin' ? 'Admin' : 'Utilisateur'}</td>
                  <td>{user.active ? 'Actif' : 'Inactif'}</td>
                  <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="icon-btn" title="Modifier" onClick={() => openEdit(user)}><Pencil size={14} /></button>
                      <button className="icon-btn" title={user.active ? 'Désactiver' : 'Activer'} onClick={() => toggleStatus(user)}>
                        {user.active ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                      {user.id !== 'admin-001' && (
                        <button className="icon-btn icon-btn-danger" title="Supprimer" onClick={() => setDeleteTarget(user.id)}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="confirm-overlay" onClick={closeForm}>
          <div className="confirm-dialog" style={{ maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h3>{editingUser ? 'Modifier Utilisateur' : 'Nouvel Utilisateur'}</h3>
            <form onSubmit={submitForm}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required disabled={!!editingUser} />
              </div>
              <div className="form-group">
                <label>Nom</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Mot de passe {editingUser && '(laisser vide pour ne pas changer)'}</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editingUser} minLength={6} />
              </div>
              <div className="form-group">
                <label>Rôle</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} disabled={editingUser?.id === 'admin-001'}>
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="form-group">
                <label>Centrales affectées</label>
                {plantsLoading ? (
                  <div className="summary-sub">Chargement des affectations...</div>
                ) : plants.length === 0 ? (
                  <div className="summary-sub">Aucune centrale disponible</div>
                ) : (
                  <div className="checkbox-grid">
                    {plants.map(p => (
                      <label key={p.plant_id} className="checkbox-item">
                        <input type="checkbox" checked={form.plantIds.includes(p.plant_id)} onChange={() => togglePlant(p.plant_id)} />
                        {p.name || p.plant_id}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Notifications</label>
                <div className="summary-card" style={{ padding: 12 }}>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: form.notifications.channels.email || form.notifications.channels.telegram ? 10 : 0 }}>
                    <label className="checkbox-item">
                      <input type="checkbox" checked={form.notifications.channels.inApp} onChange={() => toggleChannel('inApp')} /> Alertes dans l'application
                    </label>
                    <label className="checkbox-item">
                      <input type="checkbox" checked={form.notifications.channels.email} onChange={() => toggleChannel('email')} /> Alertes par email
                    </label>
                    <label className="checkbox-item">
                      <input type="checkbox" checked={form.notifications.channels.telegram} onChange={() => toggleChannel('telegram')} /> Alertes par Telegram
                    </label>
                  </div>

                  {form.notifications.channels.email && (
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block', marginBottom: 4 }}>Email de notification</label>
                      <input
                        type="email"
                        placeholder="peut être différent de l'email de connexion"
                        value={form.notifications.email}
                        onChange={e => updateNotifField('email', e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {form.notifications.channels.telegram && (
                    <div>
                      <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block', marginBottom: 4 }}>Telegram Chat ID</label>
                      <input
                        type="text"
                        placeholder="ex: 123456789"
                        value={form.notifications.telegramChatId}
                        onChange={e => updateNotifField('telegramChatId', e.target.value)}
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="confirm-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Annuler</button>
                <button type="submit" className="btn-primary">{editingUser ? 'Mettre à jour' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Supprimer l'utilisateur"
        message="Cette action est irréversible. Confirmer la suppression ?"
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
